import { Content } from '@radix-ui/react-dialog'
import { dateStringSchema, today } from '@seen/core/dates'
import {
  CONDITION_CLINICAL_STATUSES,
  ConditionClinicalStatus,
} from '@seen/core/models/conditions/constants'
import { Button } from '@seen/ui'
import { type JSX, useEffect } from 'react'
import { z } from 'zod'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogOverlay } from '#src/components/core/dialog/display/DialogOverlay.tsx'
import { DialogPortal } from '#src/components/core/dialog/display/DialogPortal.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormLabel } from '#src/components/core/form/FormLabel.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { useForm } from '#src/hooks/useForm.ts'

const CLINICAL_STATUS_OPTIONS = [
  ConditionClinicalStatus.ACTIVE,
  ConditionClinicalStatus.CONTROLLED,
  ConditionClinicalStatus.RESOLVED,
].map((status) => ({
  label: CONDITION_CLINICAL_STATUSES[status].display,
  value: status,
  keywords: [CONDITION_CLINICAL_STATUSES[status].display.toLowerCase()],
}))

const formSchema = z.object({
  onsetDate: dateStringSchema,
  clinicalStatus: z.nativeEnum(ConditionClinicalStatus),
})

export type ConditionCreateConfirmValues = z.infer<typeof formSchema>

interface ConditionCreateConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  conditionTitle: string
  onConfirm: (values: ConditionCreateConfirmValues) => void | Promise<void>
  isPending?: boolean
}

// Sit at `z-portal` (Tailwind: 200) so this dialog and any popovers it hosts
// (Combobox, DatePicker) all share one stacking layer. DOM order then handles
// the rest: the picker popover that opened the dialog renders below us, and
// any popovers we open render above us, automatically.
const CONTENT_CLASS =
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed left-1/2 top-[18dvh] z-portal grid w-full max-w-md -translate-x-1/2 gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in sm:rounded-lg'

function defaultValues(): ConditionCreateConfirmValues {
  return {
    onsetDate: today(),
    clinicalStatus: ConditionClinicalStatus.ACTIVE,
  }
}

export function ConditionCreateConfirmDialog({
  open,
  onOpenChange,
  conditionTitle,
  onConfirm,
  isPending = false,
}: ConditionCreateConfirmDialogProps): JSX.Element {
  const form = useForm({
    schema: formSchema,
    defaultValues: defaultValues(),
  })

  useEffect(() => {
    if (open) {
      form.reset(defaultValues())
    }
  }, [open, form])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-portal" />
        <Content className={CONTENT_CLASS} aria-describedby={undefined}>
          <div
            data-fake-focus-trap="true"
            // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- catches Radix's auto-focus on dialog open so no visible field shows a focus ring; Tab moves on to the real first focusable child
            tabIndex={0}
            className="absolute top-[-9999px] left-[-9999px] h-0 w-0"
          />
          <Form onSubmit={form.handleSubmit(onConfirm)}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <DialogTitle>Add Condition</DialogTitle>
                <DialogDescription>{conditionTitle}</DialogDescription>
              </div>
              <div className="flex flex-col gap-1.5">
                <FormLabel>Onset date</FormLabel>
                <FormField
                  control={form.control}
                  name="onsetDate"
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      onChange={(value) => value && field.onChange(value)}
                      className="w-full justify-start"
                      clearText={null}
                    />
                  )}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <FormLabel>Clinical status</FormLabel>
                <FormField
                  control={form.control}
                  name="clinicalStatus"
                  render={({ field }) => (
                    <Combobox<ConditionClinicalStatus>
                      options={CLINICAL_STATUS_OPTIONS}
                      onSelect={(value) => value && field.onChange(value)}
                    >
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between"
                      >
                        {CONDITION_CLINICAL_STATUSES[field.value].display}
                      </Button>
                    </Combobox>
                  )}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isPending}
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" loading={isPending}>
                  Create
                </Button>
              </div>
            </div>
          </Form>
        </Content>
      </DialogPortal>
    </Dialog>
  )
}
